import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { PageHead, Lede, Card, Note } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { QueryState } from "../../components/States";
import { useHaltState } from "../../api/queries";
import { isRetryable } from "../../api/errors";
import { pct, short, ts } from "../../lib/format";
import {
  LAYERS,
  LAYER_LABEL,
  endOfDay,
  portfolioScopeLabel,
  readOverrides,
  readPaperRuns,
  readRiskDailyReport,
  readRiskLimits,
  type RiskLimitsPayload,
} from "./api";

const READ = { retry: (n: number, e: unknown) => n < 2 && isRetryable(e), retryDelay: 1_500 } as const;

export function RiskScreen(): ReactNode {
  const limits = useQuery({ queryKey: ["g3", "risk-limits"], queryFn: readRiskLimits, ...READ });
  const paper = useHaltState();
  const overrides = useQuery({ queryKey: ["g3", "risk-overrides"], queryFn: readOverrides, ...READ });
  return (
    <>
      <PageHead
        title="三层限额"
        sub={limits.status === "success" ? `digest ${short(limits.data.digest, 12)} · ${limits.data.path}` : "GET /risk/limits"}
      />
      <Lede>
        上限只从配置来（只读，不提供界面改上限）：纸上盘与实盘读同一份 <code>config/risk_limits.json</code>。亏损上限为现金口径，组合回撤为权益口径（逐 bar 峰值）。
      </Lede>
      <QueryState status={limits.status} error={limits.error} what="风险限额（GET /risk/limits）" refetch={() => void limits.refetch()}>
        {limits.status === "success" ? <LimitsTable limits={limits.data} /> : null}
      </QueryState>

      <div style={{ marginTop: 12 }}>
        <Card title="当前占用（各策略最近一个交易日）" sub="GET /paper/state → /runs → /risk/report">
          <QueryState status={paper.status} error={paper.error} what="纸上盘策略（GET /paper/state）" refetch={() => void paper.refetch()}>
            {paper.status === "success" ? (
              Object.keys(paper.data.strategies).length === 0 ? (
                <div className="empty">还没有纸上盘策略</div>
              ) : (
                Object.entries(paper.data.strategies).map(([sid, item]) => (
                  <StrategyOccupancy key={sid} sid={sid} date={item.last_trade_date} digest={limits.status === "success" ? limits.data.digest : null} />
                ))
              )
            ) : null}
          </QueryState>
        </Card>
      </div>

      <div style={{ marginTop: 12 }}>
        <Card
          title="临时放行"
          sub={limits.status === "success" ? `有效期上限 override.max_ttl_hours = ${limits.data.override.max_ttl_hours} 小时` : "GET /risk/overrides"}
          nopad
        >
          <QueryState status={overrides.status} error={overrides.error} what="放行记录（GET /risk/overrides）" refetch={() => void overrides.refetch()}>
            {overrides.status === "success" ? (
              <Table
                empty="没有放行记录"
                cols={[{ label: "scope", mono: true }, { label: "放行人" }, { label: "理由", wrap: true }, { label: "放行时间" }, { label: "到期" }, { label: "是否有效" }]}
                rows={overrides.data.map((o, i) => ({
                  key: `${o.granted_utc}-${i}`,
                  cells: [
                    o.scope,
                    o.operator,
                    o.reason,
                    o.granted_utc,
                    o.expires_utc,
                    o.active === null ? <span className="faint">缺（服务端 active 待加）</span> : <Pill tone={o.active ? "ok" : "neutral"}>{o.active ? "有效" : "已过期"}</Pill>,
                  ],
                }))}
              />
            ) : null}
          </QueryState>
        </Card>
      </div>

      <div style={{ marginTop: 12 }}>
        <Note>
          本屏只读：临时放行的登记入口在人工放行屏（放行不解除停机，停机恢复仍走停机屏）。
          {" "}
          <a href="#/approvals">去人工放行屏登记 →</a>
        </Note>
      </div>
    </>
  );
}

function LimitsTable({ limits }: { limits: RiskLimitsPayload }): ReactNode {
  return (
    <Card title="限额表" sub="配对两腿各按单腿敞口校验（|持仓市值| / 起始资金），不按对冲后的净敞口放宽" nopad>
      <Table
        cols={[{ label: "层" }, { label: "敞口上限", num: true }, { label: "亏损 / 回撤上限", num: true }, { label: "口径" }, { label: "越界动作" }, { label: "说明", wrap: true }]}
        rows={[
          { key: "instrument", cells: ["标的", pct(limits.instrument.max_weight, 0), pct(limits.instrument.max_loss_pct, 0), "现金口径", limits.instrument.on_breach, "按标的逐个校验"] },
          {
            key: "strategy",
            cells: [
              "策略",
              pct(limits.strategy.max_gross_weight, 0),
              pct(limits.strategy.max_loss_pct, 0),
              "现金口径",
              limits.strategy.on_breach,
              limits.strategy.reduce_to === null ? "—" : `减仓到 ${pct(limits.strategy.reduce_to, 0)}`,
            ],
          },
          {
            key: "portfolio",
            cells: [
              "组合",
              pct(limits.portfolio.max_gross_weight, 0),
              pct(limits.portfolio.max_drawdown, 0),
              "合并权益口径（逐 bar 峰值）",
              limits.portfolio.on_breach,
              limits.portfolio_scope
                ? `跨策略汇总：${limits.portfolio_scope.strategies.join("、") || "无在跑策略"}；合并越界整体停机，已停机的跳过`
                : "口径缺（后端未给 portfolio_scope）",
            ],
          },
        ]}
      />
    </Card>
  );
}

function StrategyOccupancy({ sid, date, digest }: { sid: string; date: string | null; digest: string | null }): ReactNode {
  const runs = useQuery({ queryKey: ["g3", "paper-runs", sid], queryFn: () => readPaperRuns(sid), enabled: Boolean(date), ...READ });
  const report = useQuery({ queryKey: ["g3", "risk-report", sid, date], queryFn: () => readRiskDailyReport(sid, date as string), enabled: Boolean(date), ...READ });
  if (!date) return <Note>{sid}：还没有纸上盘运行</Note>;
  const run = runs.status === "success" ? runs.data.find((r) => r.trade_date === date) ?? null : null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ marginBottom: 4 }}>
        <b>{sid}</b> · {date} · 风控 {run?.risk ? <Pill tone={run.risk === "已接入" ? "ok" : "bad"}>{run.risk}</Pill> : <span className="faint">缺</span>}
        {" "}
        <a href={`#/riskreport?strategy=${encodeURIComponent(sid)}&date=${date}`}>看完整核对 →</a>
      </div>
      <QueryState status={report.status} error={report.error} what={`${sid} 风险报告`} refetch={() => void report.refetch()}>
        {report.status === "success" ? (
          "missing" in report.data ? (
            <Note tone="bad">
              {run?.risk === "未接入" ? "该日运行未接入风控，没有风险报告" : `没有该日风险报告：${report.data.missing}`}
            </Note>
          ) : (
            (() => {
              const r = report.data.report;
              const eod = endOfDay(r);
              const halted = r.actions.some((a) => a.action === "halt");
              return (
                <>
                  <Table
                    cols={[{ label: "层" }, { label: "日终占用", num: true }, { label: "峰值占用", num: true }, { label: "峰值时刻" }, { label: "状态" }]}
                    rows={LAYERS.map((layer) => ({
                      key: layer,
                      cls: r.layers[layer].breached ? "bad" : "",
                      cells: [
                        layer === "portfolio" ? `${LAYER_LABEL[layer]}（${portfolioScopeLabel(r.portfolio_scope)}）` : LAYER_LABEL[layer],
                        pct(eod[layer]),
                        pct(r.layers[layer].peak_utilization),
                        r.layers[layer].peak_bar ? ts(r.layers[layer].peak_bar!.ts_event) : "—",
                        <Pill tone={r.layers[layer].breached ? "bad" : "ok"}>{r.layers[layer].breached ? "越界" : "未越界"}</Pill>,
                      ],
                    }))}
                  />
                  <Note tone={digest && r.limits_digest !== digest ? "warn" : ""}>
                    报告 limits_digest {short(r.limits_digest, 12)}
                    {digest ? (r.limits_digest === digest ? "，与当前配置一致" : `，与当前配置 ${short(digest, 12)} 不一致`) : ""}
                    {halted ? <> · <a href="#/halt">风险上限触发停机 · 去停机屏 →</a></> : null}
                  </Note>
                  <Table
                    empty={`${date} 没有被风控拒绝的订单`}
                    cols={[{ label: "开仓前拦截 · 标的", mono: true }, { label: "层" }, { label: "原因", wrap: true }, { label: "时间" }]}
                    rows={r.rejections.map((x, i) => ({ key: `rej-${i}`, cells: [x.instrument_id, x.layer, x.reason, ts(x.ts_event)] }))}
                  />
                  <Table
                    empty={`${date} 没有触发减仓 / 停机 / 放行`}
                    cols={[{ label: "强制动作 · 层" }, { label: "动作" }, { label: "原因", wrap: true }, { label: "值 / 上限", num: true }, { label: "时间" }]}
                    rows={r.actions.map((x, i) => ({
                      key: `act-${i}`,
                      cells: [x.layer, x.action, x.reason || "—", x.value === null ? "—" : `${pct(x.value)} / ${pct(x.limit)}`, x.ts_event === null ? "—" : ts(x.ts_event)],
                    }))}
                  />
                </>
              );
            })()
          )
        ) : null}
      </QueryState>
    </div>
  );
}
